import { Theme, useTheme } from '@nexelem/deprecated-theme';
import { DataGridProProps, GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { defaultsDeep } from 'lodash';
import { ComponentProps, ReactNode, useMemo } from 'react';
import { TableProps } from '../../Table';
import { Footer, getCell, getHeader, getScroll, StyledCheckbox, useTableRow } from '../../TableElements/components';
import { CellGrouping } from '../../utils/cells';
import { TableLoadingOverlay } from '../../TableElements/components/TableLoadingOverlay';
import { MultiselectFooterProps } from '../../TableElements/components/Footer/MultiselectFooter';
declare module '@mui/x-data-grid-pro' {
  interface FooterPropsOverrides extends MultiselectFooterProps, ComponentProps<typeof Footer> {}
}

type NotChangingTableProps = Omit<
  TableProps,
  'isLoading' | 'columns' | 'pinnedColumns' | 'groupColumnName' | 'rows' | 'isHoverable'
>;

function getRequiredColumnName(
  groupColumnName: string | undefined,
): Pick<DataGridProProps, 'getRowClassName' | 'getTreeDataPath'> {
  if (groupColumnName) {
    return {
      getRowClassName: (params: GridRowClassNameParams) =>
        params.row[groupColumnName]?.length > 1 ? 'rowGrouped' : 'singleRow',
      getTreeDataPath: (row) => row[groupColumnName],
    };
  }
  return {};
}

export const useDataGridProps = (
  groupColumnName: string | undefined,
  isMultiselect: boolean,
  NoRowsOrResultsOverlayComponent: () => ReactNode,
  isHoverable: boolean | undefined,
  { sx, TableLegend, MultiSelectFooterProps, withCancel, ...props }: NotChangingTableProps,
): Partial<Omit<DataGridProProps, 'columns' | 'rows'>> => {
  const theme: Theme = useTheme();
  const row = useTableRow(isHoverable);

  return useMemo(
    () => {
      const requiredColumnName: Pick<DataGridProProps, 'getRowClassName' | 'getTreeDataPath'> =
        getRequiredColumnName(groupColumnName);
      const typedTheme = theme as Theme;
      const stateProps: Omit<DataGridProProps, 'columns' | 'rows'> = {
        disableColumnMenu: true,
        checkboxSelection: isMultiselect,
        // TODO: talk with Zosia - pricing select row by clicking on row
        disableRowSelectionOnClick: !isMultiselect,
        sx: [getHeader(typedTheme), getCell(typedTheme), getScroll(typedTheme), sx as any].filter(Boolean),
        groupingColDef: {
          headerName: '',
          width: 40,
          renderCell: (params) => <CellGrouping {...params} />,
          cellClassName: 'tree-data-group-cell',
        },
        experimentalFeatures: {},
        slots: {
          footer: Footer,
          row: row,
          baseCheckbox: StyledCheckbox,
          baseTooltip: ({ children }) => children,
          loadingOverlay: TableLoadingOverlay,
          noRowsOverlay: NoRowsOrResultsOverlayComponent,
          noResultsOverlay: NoRowsOrResultsOverlayComponent,
        },
        slotProps: {
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
          footer: {
            TableLegend,
            MultiSelectFooterProps,
            withCancel,
          },
        },
      };

      return defaultsDeep(stateProps, requiredColumnName, props);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      groupColumnName,
      isMultiselect,
      isHoverable,
      MultiSelectFooterProps,
      TableLegend,
      // memoization is not working when props are in deps
      // I extracted all currently changing props and provided directly to grid
      // props,
    ],
  );
};
